import FacePageHeader from '@/components/layout/FacePageHeader';
import Nav from '@/components/navigation/Nav';
import PageIntro from '@/components/layout/PageIntro';
import PageLoadingState from '@/components/ui/PageLoadingState';
import ShipmentsList from './components/ShipmentsList';
import ShipmentsToolbar from './components/ShipmentsToolbar';
import {
  clearShipmentsListMemory,
  getShipmentsFilterFromQuery,
  getShipmentsFilterFromTab,
  getShipmentsListMemory,
  getShipmentsListMemoryKey,
  getShipmentsStatusesFromFilter,
  getShipmentsTabFromFilter,
  mapShipmentCountsToTabCounts,
  mapShipmentListItemApiToShipmentListItem,
  saveShipmentsListMemory,
  SHIPMENTS_PAGE_SIZE,
  SHIPMENTS_TABS,
  ShipmentsListMemory,
  ShipmentsTabValue
} from './model';
import { cn } from '@/lib/utils';
import { getShipmentCounts, getShipments } from '@/features/Shipment/api';
import { MouseEvent, PointerEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageFooter, PageHeader, PageMain, PageShell } from '@/components/layout/PageShell';
import { Tabs, TabsContent, TabsList } from '@/components/ui/tabs';
import { toast } from '@/components/ui/sonner';
import { useAiChatContext } from '@/features/AiChat/model/context';
import { useAuthStore } from '@/features/Auth/model/authStore';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const useDebouncedValue = <TValue,>(value: TValue, delay: number): TValue => {
  const [debouncedValue, setDebouncedValue] = useState<TValue>(value);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [delay, value]);

  return debouncedValue;
};

const ShipmentsPage = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenData = useAuthStore((state) => state.tokenData);
  const {
    methods: {
      closeChatSession,
      openChat
    }
  } = useAiChatContext();

  const orgId = tokenData?.org_id ?? null;
  const activeFilter = getShipmentsFilterFromQuery(searchParams.get('filter'));
  const activeTab = getShipmentsTabFromFilter(activeFilter);
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 400);
  const normalizedSearch = debouncedSearchQuery.trim();
  const shipmentsListMemoryKey = getShipmentsListMemoryKey(location.pathname, activeFilter);
  const shipmentsStatuses = useMemo(() => {
    return getShipmentsStatusesFromFilter(activeFilter);
  }, [activeFilter]);
  const restoreMemoryRef = useRef<ShipmentsListMemory | null>(getShipmentsListMemory(shipmentsListMemoryKey));
  const tabsDragStateRef = useRef({
    isDragging: false,
    scrollLeft: 0,
    startX: 0
  });
  const tabsListRef = useRef<HTMLDivElement | null>(null);
  const wasTabsDraggedRef = useRef<boolean>(false);
  const wasScrollRestoredRef = useRef<boolean>(false);

  const {
    data: dataGetShipments,
    fetchNextPage: fetchNextPageGetShipments,
    hasNextPage: hasNextPageGetShipments,
    isError: isErrorGetShipments,
    isFetchingNextPage: isFetchingNextPageGetShipments,
    isLoading: isLoadingGetShipments
  } = useInfiniteQuery({
    enabled: Boolean(orgId),
    getNextPageParam: (lastPage, allPages) => {
      const loadedItemsCount = allPages.reduce((count, page) => count + page.items.length, 0);

      if (loadedItemsCount >= lastPage.total_count) return undefined;

      return (lastPage.page ?? allPages.length) + 1;
    },
    initialPageParam: 1,
    queryFn: ({ pageParam }) => getShipments({
      filters: [
        {
          name: 'status',
          value: shipmentsStatuses
        },
        ...(normalizedSearch
          ? [{
              name: 'invoice_number' as const,
              value: normalizedSearch
            }]
          : [])
      ],
      org_id: orgId!,
      page: pageParam,
      page_size: SHIPMENTS_PAGE_SIZE
    }),
    queryKey: ['getShipments', location.pathname, orgId, activeFilter, normalizedSearch, SHIPMENTS_PAGE_SIZE],
    retry: 1
  });

  const {
    data: dataGetShipmentCounts
  } = useQuery({
    enabled: Boolean(orgId),
    queryFn: () => getShipmentCounts({
      org_id: orgId!
    }),
    queryKey: ['getShipmentCounts', location.pathname, orgId],
    retry: 1
  });

  const loadedPagesCount = dataGetShipments?.pages.length ?? 0;
  const shipments = useMemo(() => {
    return dataGetShipments?.pages.flatMap((page) => {
      return page.items.map(mapShipmentListItemApiToShipmentListItem);
    }) ?? [];
  }, [dataGetShipments?.pages]);
  const tabCounts = useMemo(() => {
    return mapShipmentCountsToTabCounts(dataGetShipmentCounts);
  }, [dataGetShipmentCounts]);

  useEffect(() => {
    if (!isErrorGetShipments) return;

    toast.error('Поставки недоступны');
  }, [isErrorGetShipments]);

  useEffect(() => {
    const restoreMemory = restoreMemoryRef.current;

    if (!restoreMemory || wasScrollRestoredRef.current) return;

    if (
      loadedPagesCount < restoreMemory.loadedPages
      && hasNextPageGetShipments
      && !isFetchingNextPageGetShipments
    ) {
      fetchNextPageGetShipments();

      return;
    }

    const targetShipment = shipments.find((shipment) => shipment.id === restoreMemory.shipmentId);

    if (!targetShipment && hasNextPageGetShipments && !isFetchingNextPageGetShipments) {
      fetchNextPageGetShipments();

      return;
    }

    requestAnimationFrame(() => {
      const targetElement = document.querySelector(`[data-shipment-id="${restoreMemory.shipmentId}"]`);

      if (targetElement) {
        targetElement.scrollIntoView({
          block: 'center'
        });
      } else {
        window.scrollTo(0, restoreMemory.scrollY);
      }

      wasScrollRestoredRef.current = true;
      clearShipmentsListMemory(shipmentsListMemoryKey);
    });
  }, [
    fetchNextPageGetShipments,
    hasNextPageGetShipments,
    isFetchingNextPageGetShipments,
    loadedPagesCount,
    shipmentsListMemoryKey,
    shipments
  ]);

  const handleChangeTab = useCallback((value: string): void => {
    const filter = getShipmentsFilterFromTab(value as ShipmentsTabValue);

    navigate(`/shipments?filter=${filter}`);
  }, [navigate]);

  const handleCreateShipment = useCallback((): void => {
    closeChatSession();
    openChat();
  }, [closeChatSession, openChat]);

  const handleLoadMoreShipments = useCallback((): void => {
    if (!hasNextPageGetShipments || isFetchingNextPageGetShipments) return;

    fetchNextPageGetShipments();
  }, [fetchNextPageGetShipments, hasNextPageGetShipments, isFetchingNextPageGetShipments]);

  const handleClickTabsList = useCallback((event: MouseEvent<HTMLDivElement>): void => {
    if (!wasTabsDraggedRef.current) return;

    event.stopPropagation();
    wasTabsDraggedRef.current = false;
  }, []);

  const handleOpenShipment = useCallback((shipmentId: string): void => {
    saveShipmentsListMemory(shipmentsListMemoryKey, {
      loadedPages: Math.max(loadedPagesCount, 1),
      scrollY: window.scrollY,
      shipmentId
    });

    navigate(`/shipments/${shipmentId}`);
  }, [loadedPagesCount, navigate, shipmentsListMemoryKey]);

  const handlePointerDownTabsList = useCallback((event: PointerEvent<HTMLDivElement>): void => {
    if (event.pointerType !== 'mouse') return;

    const tabsList = tabsListRef.current;

    if (!tabsList || tabsList.scrollWidth <= tabsList.clientWidth) return;

    tabsDragStateRef.current = {
      isDragging: true,
      scrollLeft: tabsList.scrollLeft,
      startX: event.clientX
    };
    wasTabsDraggedRef.current = false;
  }, []);

  const handlePointerMoveTabsList = useCallback((event: PointerEvent<HTMLDivElement>): void => {
    const dragState = tabsDragStateRef.current;
    const tabsList = tabsListRef.current;

    if (!dragState.isDragging || event.pointerType !== 'mouse' || !tabsList) return;

    const deltaX = event.clientX - dragState.startX;

    if (Math.abs(deltaX) <= 4) return;

    wasTabsDraggedRef.current = true;
    event.preventDefault();
    tabsList.scrollLeft = dragState.scrollLeft - deltaX;
  }, []);

  const handlePointerUpTabsList = useCallback((): void => {
    tabsDragStateRef.current.isDragging = false;
  }, []);

  return (
    <PageShell
      contentVariant="face"
      footerMode="fixed"
      headerMode="static"
    >
      <PageHeader>
        <FacePageHeader />
      </PageHeader>

      <PageMain contentClassName="flex flex-1 flex-col gap-6">
        <PageIntro title="Поставки" />
        <ShipmentsToolbar
          onCreateShipment={handleCreateShipment}
          onSearchChange={setSearchQuery}
          searchValue={searchQuery}
        />
        <Tabs
          className="flex flex-col gap-6"
          value={activeTab}
        >
          <TabsList
            className="flex h-auto w-full justify-start gap-2 overflow-x-auto px-1 py-0.5 bg-transparent rounded-none cursor-grab select-none active:cursor-grabbing min-[769px]:px-0 min-[769px]:py-0 min-[769px]:cursor-default min-[769px]:select-auto min-[769px]:overflow-visible [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            onClickCapture={handleClickTabsList}
            onPointerCancel={handlePointerUpTabsList}
            onPointerDown={handlePointerDownTabsList}
            onPointerMove={handlePointerMoveTabsList}
            onPointerUp={handlePointerUpTabsList}
            ref={tabsListRef}
          >
            {SHIPMENTS_TABS.map((tab) => (
              <button
                aria-selected={activeTab === tab.value}
                className={cn(
                  'inline-flex items-center justify-center ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
                  'shrink-0 gap-2 px-3 py-1 whitespace-nowrap min-[769px]:min-w-0 min-[769px]:flex-1',
                  'font-sans text-sm font-semibold leading-5',
                  'rounded-xl',
                  'transition-colors',
                  'data-[state=active]:bg-accent data-[state=active]:text-on-accent',
                  'data-[state=active]:shadow-none data-[state=active]:hover:bg-accent-hover',
                  'data-[state=inactive]:bg-muted data-[state=inactive]:text-muted-foreground',
                  'data-[state=inactive]:hover:bg-muted/80'
                )}
                data-state={activeTab === tab.value ? 'active' : 'inactive'}
                key={tab.value}
                onClick={() => handleChangeTab(tab.value)}
                role="tab"
                tabIndex={activeTab === tab.value ? 0 : -1}
                type="button"
              >
                <span className="whitespace-nowrap min-[769px]:truncate">{tab.label}</span>
                <span className="opacity-70">{tabCounts[tab.value]}</span>
              </button>
            ))}
          </TabsList>
          {SHIPMENTS_TABS.map((tab) => (
            <TabsContent
              className="mt-0"
              key={tab.value}
              value={tab.value}
            >
              {activeTab === tab.value && (
                isLoadingGetShipments ? (
                  <PageLoadingState
                    className="min-h-[40vh]"
                    label="Загрузка поставок"
                  />
                ) : (
                  <ShipmentsList
                    hasNextPage={Boolean(hasNextPageGetShipments)}
                    isError={isErrorGetShipments}
                    isFetchingNextPage={isFetchingNextPageGetShipments}
                    items={shipments}
                    onLoadMore={handleLoadMoreShipments}
                    onOpenShipment={handleOpenShipment}
                  />
                )
              )}
            </TabsContent>
          ))}
        </Tabs>
      </PageMain>

      <PageFooter>
        <Nav />
      </PageFooter>
    </PageShell>
  );
};

export default ShipmentsPage;